import * as THREE from 'three';

/**
 * Bake rotations+translations into cloned box geometries and concatenate them
 * into one BufferGeometry (one draw call). The source geometries are disposed.
 */
export function mergeBoxes(
  parts: Array<{
    geo: THREE.BoxGeometry;
    pos: [number, number, number];
    rot?: [number, number, number];
  }>,
): THREE.BufferGeometry {
  const merged: THREE.BufferGeometry[] = parts.map(({ geo, pos, rot }) => {
    const g = geo.clone();
    if (rot) {
      if (rot[0]) g.rotateX(rot[0]);
      if (rot[1]) g.rotateY(rot[1]);
      if (rot[2]) g.rotateZ(rot[2]);
    }
    g.translate(pos[0], pos[1], pos[2]);
    return g;
  });
  for (const { geo } of parts) geo.dispose();
  let result = merged[0];
  for (let i = 1; i < merged.length; i += 1) {
    result = concatGeometry(result, merged[i]);
  }
  return result;
}

/** Manual concatenation to avoid pulling BufferGeometryUtils for a few shapes. */
export function concatGeometry(
  a: THREE.BufferGeometry,
  b: THREE.BufferGeometry,
): THREE.BufferGeometry {
  const out = new THREE.BufferGeometry();
  for (const name of ['position', 'normal', 'uv'] as const) {
    const attrA = a.getAttribute(name) as THREE.BufferAttribute | undefined;
    const attrB = b.getAttribute(name) as THREE.BufferAttribute | undefined;
    if (!attrA || !attrB) continue;
    const array = new Float32Array(attrA.array.length + attrB.array.length);
    array.set(attrA.array as Float32Array, 0);
    array.set(attrB.array as Float32Array, attrA.array.length);
    out.setAttribute(name, new THREE.BufferAttribute(array, attrA.itemSize));
  }
  const indexA = a.getIndex();
  const indexB = b.getIndex();
  if (indexA && indexB) {
    const offset = (a.getAttribute('position') as THREE.BufferAttribute).count;
    const array = new Uint32Array(indexA.count + indexB.count);
    array.set(indexA.array as unknown as ArrayLike<number>, 0);
    for (let i = 0; i < indexB.count; i += 1) {
      array[indexA.count + i] = (indexB.array[i] as number) + offset;
    }
    out.setIndex(new THREE.BufferAttribute(array, 1));
  }
  a.dispose();
  b.dispose();
  return out;
}
